import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { X, DollarSign, Calendar } from 'lucide-react';
import saasApiService from '../../../services/saasApi';

const schema = yup.object({
  client_id: yup.number().required('Cliente es requerido').min(1, 'Seleccione un cliente'),
  vehicle_id: yup.number().optional(),
  amount: yup.number().required('Monto es requerido').min(0.01, 'Monto debe ser mayor a 0'),
  service_date: yup.string().required('Fecha de servicio es requerida'),
  invoice_number: yup.string().optional(),
  payment_method: yup
    .string()
    .oneOf(['cash','transfer','check','card','other',''], 'Método no válido')
    .when('status', {
      is: (val: string) => val === 'paid',
      then: (schema) => schema.required('Método de pago es requerido cuando el estado es Pagado').oneOf(['cash','transfer','check','card','other'], 'Método no válido'),
      otherwise: (schema) => schema.optional()
    }),
  status: yup.string().oneOf(['pending','paid','overdue','cancelled']).optional(),
  remission_guide_number: yup.string().optional(),
  sender_guide_number: yup.string().optional(),
  origin: yup.string().optional(),
  destination: yup.string().optional(),
  notes: yup.string().optional()
});

interface RevenueFormProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: () => void;
  revenue?: any;
}

const RevenueForm: React.FC<RevenueFormProps> = ({ isOpen, onClose, onSave, revenue }) => {
  const [clients, setClients] = useState<any[]>([]);
  const [vehicles, setVehicles] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [isOutsourcing, setIsOutsourcing] = useState<boolean>(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue
  } = useForm({
    resolver: yupResolver(schema)
  });

  useEffect(() => {
    if (isOpen) {
      fetchClients();
      fetchVehicles();
      if (revenue) {
        setValue('client_id', revenue.client_id || '');
        setValue('vehicle_id', revenue.vehicle_id || '');
        setValue('amount', revenue.amount || '');
        setValue('service_date', revenue.service_date ? revenue.service_date.split('T')[0] : '');
        setValue('invoice_number', revenue.invoice_number || '');
        setValue('payment_method', revenue.payment_method || '');
        setValue('status', revenue.status || 'pending');
        setValue('remission_guide_number', revenue.remission_guide_number || '');
        setValue('sender_guide_number', revenue.sender_guide_number || '');
        setValue('origin', revenue.origin || '');
        setValue('destination', revenue.destination || '');
        setValue('notes', revenue.notes || '');
      } else {
        reset();
        // Establecer fecha de servicio por defecto a hoy
        setValue('service_date', new Date().toISOString().split('T')[0]);
        setValue('status', 'pending');
        setIsOutsourcing(false);
      }
    }
  }, [isOpen, revenue, setValue, reset]);

  const fetchClients = async () => {
    try {
      const response = await saasApiService.getClients();
      console.log('Clients response in RevenueForm:', response);
      
      // Manejar diferentes estructuras de respuesta
      const clientsData = Array.isArray(response) ? response : 
                         (response?.items && Array.isArray(response.items)) ? response.items :
                         (response?.data?.items && Array.isArray(response.data.items)) ? response.data.items : [];
      
      setClients(clientsData);
    } catch (error) {
      console.error('Error fetching clients:', error);
      setClients([]);
    }
  };

  const fetchVehicles = async () => {
    try {
      const response = await saasApiService.getVehicles();
      // Soportar múltiples formas de respuesta
      const items = Array.isArray(response)
        ? response
        : (response?.vehicles || response?.data?.vehicles || response?.data?.data?.vehicles || []);
      console.log('Vehicles response in RevenueForm:', response, 'parsed items:', items);
      setVehicles(items);
    } catch (e) {
      console.error('Error fetching vehicles:', e);
      setVehicles([]);
    }
  };

  const onSubmit = async (data: any) => {
    try {
      setLoading(true);
      
      // Preparar payload
      const formData: any = {
        ...data,
        client_id: parseInt(data.client_id),
        vehicle_id: data.vehicle_id ? parseInt(data.vehicle_id) : undefined,
        amount: parseFloat(data.amount),
        status: data.status || 'pending'
      };

      // Si es tercerización, no enviar vehicle_id
      if (isOutsourcing) {
        delete formData.vehicle_id;
      }

      // Si se crea como pagado, incluir payment_date hoy si no se envía
      if (formData.status === 'paid' && !formData['payment_date']) {
        formData['payment_date'] = new Date().toISOString();
      }
      
      console.log('Sending revenue data:', formData, 'service_date=', formData['service_date']);
      
      if (revenue) {
        await saasApiService.updateRevenue(revenue.id, formData);
      } else {
        await saasApiService.createRevenue(formData);
      }
      
      onSave();
      onClose();
      reset();
    } catch (error: any) {
      console.error('Error saving revenue:', error?.response?.data || error);
      const base = error?.response?.data;
      const errorMessage = base?.message || 'Error al guardar el ingreso';
      const details = Array.isArray(base?.errors) ? base.errors.map((e:any)=> e.msg || e.message).join('. ') : '';
      alert(details ? `${errorMessage}. ${details}` : errorMessage);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-1/2 shadow-lg rounded-md bg-white">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-medium text-gray-900">
            {revenue ? 'Editar Ingreso' : 'Nuevo Ingreso'}
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Cliente *
              </label>
              <select
                {...register('client_id')}
                className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">Seleccionar cliente</option>
                {clients.map((client) => (
                  <option key={client.id} value={client.id}>
                    {client.company_name}
                  </option>
                ))}
              </select>
              {errors.client_id && (
                <p className="mt-1 text-sm text-red-600">{errors.client_id.message}</p>
              )}
            </div>
            {!isOutsourcing && (
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Tracto
                </label>
                <select
                  {...register('vehicle_id', { setValueAs: (v) => (v === '' ? undefined : parseInt(v, 10)) })}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">Seleccionar tracto</option>
                  {vehicles.map((v) => (
                    <option key={v.id} value={v.id}>
                      {v.brand} {v.model} ({v.license_plate})
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>

          <div className="flex items-center space-x-3 p-3 bg-gray-50 rounded-md border">
            <input
              id="isOutsourcing"
              type="checkbox"
              checked={isOutsourcing}
              onChange={(e) => setIsOutsourcing(e.target.checked)}
              className="h-4 w-4 text-blue-600 border-gray-300 rounded"
            />
            <label htmlFor="isOutsourcing" className="text-sm text-gray-700">
              Ingreso por tercerización (no requiere tracto)
            </label>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">
                <DollarSign className="inline h-4 w-4 mr-1" />
                Monto *
              </label>
              <input
                {...register('amount')}
                type="number"
                step="0.01"
                className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                placeholder="0.00"
              />
              {errors.amount && (
                <p className="mt-1 text-sm text-red-600">{errors.amount.message}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">
                Estado de Pago
              </label>
              <select
                {...register('status')}
                className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="pending">Pendiente</option>
                <option value="paid">Pagado</option>
              </select>
            </div>
          </div>

          {/* Descripción eliminada por requerimiento */}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">
                <Calendar className="inline h-4 w-4 mr-1" />
                Fecha de Servicio *
              </label>
              <input
                {...register('service_date')}
                type="date"
                className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              />
              {errors.service_date && (
                <p className="mt-1 text-sm text-red-600">{errors.service_date.message}</p>
              )}
            </div>

            {/* Fecha de Vencimiento eliminada por requerimiento */}
          </div>

          {/* Guías y Rutas */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Num. Guía Remisión</label>
              <input
                {...register('remission_guide_number')}
                type="text"
                className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Num. Guía Remitente</label>
              <input
                {...register('sender_guide_number')}
                type="text"
                className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Origen</label>
              <input
                {...register('origin')}
                type="text"
                className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Destino</label>
              <input
                {...register('destination')}
                type="text"
                className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Número de Factura
              </label>
              <input
                {...register('invoice_number')}
                type="text"
                className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                placeholder="Se genera automáticamente"
                disabled
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">
                Método de Pago
              </label>
              <select
                {...register('payment_method')}
                className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">Seleccionar método</option>
                <option value="cash">Efectivo</option>
                <option value="transfer">Transferencia</option>
                <option value="check">Cheque</option>
                <option value="card">Tarjeta</option>
                <option value="other">Otro</option>
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">
              Notas
            </label>
            <textarea
              {...register('notes')}
              rows={3}
              className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              placeholder="Notas adicionales..."
            />
          </div>

          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-200 border border-gray-300 rounded-md hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
            >
              {loading ? 'Guardando...' : (revenue ? 'Actualizar' : 'Crear Ingreso')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default RevenueForm;
